/**
 * @file   src\utils\apiUrls.ts
 * @brief  This file is responsible for defining api methods.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

// Authorization api urls.
export enum AuthApi {
  LOGIN = '/admin/signIn',
  LOGOUT = '/admin/signOut',
  FORGOT_PASSWORD = '/admin/sendEmailOtp',
  REFRESH_TOKEN = '/admin/renewToken',
  RESET_PASSWORD_TOKEN_VALIDATION = '/admin/validateOtp',
  RESET_PASSWORD = '/admin/resetPassword',
}

// Get state api urls.
export enum StateApi {
  GET_STATE = '/admin/company/getStateList/',
}

// Get company details api urls.
export enum CompanyApi {
  GET_COMPANY = '/admin/company/getCompanyDetails/',
  GET_COMPANY_ADDRESS = '/admin/company/getCompanyAddress/',
  ADD_COMPANY_BANK = '/admin/company/createBankAccount',
  GET_COMPANY_DETAILS_BY_ID = '/admin/company/companyDetailsById/',
  GET_SUPPORTED_COUNTRIES = '/admin/company/supported-countries',
  GET_COMPANY_BANK_DETAILS = '/admin/company/bankingInfo/',
  GENERATE_APP_URL = '/admin/company/generateAppUrl',
  UPDATE_PAX_CARWASH_ID = '/admin/company/updatePaxCarwashID',
  CARWASH_OWNER_COMPANY_DETAILS = '/carwash/company/details',
  CARWASH_OWNER_COMPANY_ADDRESS = '/carwash/company/getCompanyAddress',
  UPDATE_COMPANY_STATUS = '/admin/company/updateCompanyStatus',
  UPDATE_PAYMENT_METHOD = '/admin/company/update-payment-method',
  CARWASH_OWNER_GET_COMPANY_BANK_DETAILS = '/carwash/company/bankingInfo',
  CARWASH_OWNER_UPDATE_PAYMENT_METHOD = '/carwash/company/update-payment-method',
  VALIDATE_COMPANY_PAYMENT_API_KEYS = '/admin/company/validate-payment-method',
  CARWASH_OWNER_VALIDATE_COMPANY_PAYMENT_API_KEYS = '/carwash/company/validate-payment-method',
  CARWASH_OWNER_GET_CARINFO_SETTINGS='/carwash/company/getCarInfoSettings',
  CARWASH_OWNER_UPDATE_CARINFO_SETTINGS='/carwash/company/updateCarInfoSettings',
  CARWASH_OWNER_GET_OPTSPOT_SETTINGS='/carwash/company/getOptSpotSettings',
  CARWASH_OWNER_UPDATE_OPTSPOT_SETTINGS='/carwash/company/updateOptSpotExportSettings',
  CARWASH_OWNER_GET_QUEUE_SETTINGS='/carwash/location/getwashqueueingstatus/',
  CARWASH_OWNER_UPDATE_QUEUE_SETTINGS='/carwash/location/updatewashqueueingstatus',
  MULTILOCATION_REPORT_SETTINGS='/carwash/company/updateMultipleLocationSettings',
  GET_MULTILOCATION_REPORT_SETTINGS='/carwash/company/getMultipleLocationSettings',
  UPDATE_USER_PHONE_NUMBER = "/carwash/company/updateUserPhoneNumber",
  GET_COMPANY_STATUS_SETTINGS = "/carwash/company/getStatus",
  ADD_USER_CAR_INFO = "/carwash/company/addUserCarInfo",
  EDIT_USER_CAR_INFO = "/carwash/company/editUserCarInfo",
  DELETE_USER_CAR_INFO = "/carwash/company/deleteUserCarInfo",
  GET_USER_CAR_INFO = "/carwash/company/getUserCarInfo/",
  GET_PAX_SETTINGS = "/carwash/company/getPaxEnabledStatus"
}

// Location api urls.
export enum LocationApi {
  ADD_LOCATION = '/admin/location/addLocation',
  ADD_LOCATION_BANK = '/admin/location/locationBankInfo',
  ADD_LOCATION_EQUIPMENT = '/admin/bay/createBay',
  LOCATION_LIST = '/admin/location/getCompanyLocations/',
  DELETE_LOCATION = '/admin/location/deleteLocation',
  UPDATE_LOCATION = '/admin/location/editLocation',
  PAUSE_LOCATION = '/admin/location/updateLocationStatus',
  GET_SINGLE_LOCATION = '/admin/location/getLocationDetails/',
  GET_LOCATION_BANK_DETAILS = '/admin/location/locationBankingDetails/',
  GET_COMPANY_LOCATIONS = '/admin/location/getLocations/',
  UPDATE_LOCATION_PAYMENT_METHOD = '/admin/location/update-payment-method',
  CARWASH_OWNER_LOCATION_LIST = '/carwash/location/getCompanyLocations',
  CARWASH_OWNER_LOCATION_DETAILS = '/carwash/location/getLocationDetails/',
  CARWASH_OWNER_ADD_LOCATION = '/carwash/location/addLocation',
  CARWASH_OWNER_ADD_LOCATION_BANK = '/carwash/location/locationBankInfo',
  CARWASH_OWNER_ADD_LOCATION_EQUIPMENT = '/carwash/bay/createBay',
  CARWASH_OWNER_UPDATE_LOCATION = '/carwash/location/editLocation',
  CARWASH_OWNER_GET_COMPANY_LOCATIONS = '/carwash/location/getLocations/',
  GET_CATEGORIES = '/carwash/location/getCategories',
  ADMIN_GET_CATEGORIES = '/admin/location/getCategories',
  CARWASH_OWNER_PAUSE_LOCATION = '/carwash/location/updateLocationStatus',
  CARWASH_OWNER_UPDATE_LOCATION_PAYMENT_METHOD = '/carwash/location/update-payment-method',
  CARWASH_OWNER_GET_LOCATION_BANK_DETAILS = '/carwash/location/locationBankingDetails/',
  CARWASH_OWNER_DELETE_LOCATION = '/carwash/location/deleteLocation',

}

// Equipment api urls.
export enum EquipmentApi {
  EQUIPMENT_LIST = 'admin/bay/getEquipments/',
  GET_DEVICES = '/admin/bay/getDevices',
  GET_LPR_DEVICES = '/admin/bay/getLprDevices/',
  ADD_DEVICE = '/admin/bay/addDevice',
  GET_DEVICE_DETAILS = '/admin/bay/getdeviceDetails/',
  EDIT_DEVICE = '/admin/bay/editDevice',
  DELETE_DEVICE = '/admin/bay/deleteDevice',
  DELETE_EQUIPMENT = '/admin/bay/deleteEquipment',
  UPDATE_EQUIPMENT = '/admin/bay/EditEquipment',
  GET_SINGLE_EQUIPMENT = '/admin/bay/getEquipmentDetails/',
  UPDATE_EQUIPMENT_DEVICE_ID = '/admin/bay/updateDeviceId',
  SYNC_BAY_EQUIPMENT_STATUS = '/admin/bay/updateSingleDeviceStatus',
  DEVICE_LISTING_API = '/admin/bay/getWashBayDevices/',
  UPDATE_BAY_TIMING = '/admin/bay/updateBayTiming',
  GET_BAY_TIMING = '/admin/bay/getBayTiming/',
  CARWASH_OWNER_EQUIPMENT_LIST = 'carwash/bay/getEquipments/',
  CARWASH_OWNER_DEVICE_LISTING_API = '/carwash/bay/getWashBayDevices/',
  CARWASH_OWNER_UPDATE_EQUIPMENT_DEVICE_ID = '/carwash/bay/updateDeviceId',
  CARWASH_OWNER_UPDATE_EQUIPMENT = '/carwash/bay/EditEquipment',
  CARWASH_OWNER_GET_SINGLE_EQUIPMENT = '/carwash/bay/getEquipmentDetails/',
  CARWASH_OWNER_DELETE_EQUIPMENT = '/carwash/bay/deleteEquipment',
  CARWASH_OWNER_SYNC_BAY_EQUIPMENT_STATUS = '/carwash/bay/updateSingleDeviceStatus',
  CARWASH_OWNER_GET_BAY_TIMING = '/carwash/bay/getBayTiming/',
  CARWASH_OWNER_UPDATE_BAY_TIMING = '/carwash/bay/updateBayTiming',
  CARWASH_OWNER_GET_DEVICES = '/carwash/bay/getDevices',
  CARWASH_OWNER_GET_LPR_DEVICES = '/carwash/bay/getLprDevices',
  CARWASH_OWNER_ADD_LPR_DEVICE = '/carwash/bay/addLprDevice',
  CARWASH_OWNER_EDIT_LPR_DEVICE = '/carwash/bay/editLprDevice',
  CARWASH_OWNER_DELETE_LPR_DEVICE = '/carwash/bay/deleteLprDevice',
  CARWASH_OWNER_ADD_DEVICE = '/carwash/bay/addDevice',
  CARWASH_OWNER_GET_DEVICE_DETAILS = '/carwash/bay/getdeviceDetails/',
  CARWASH_OWNER_EDIT_DEVICE = '/carwash/bay/editDevice',
  CARWASH_OWNER_DELETE_DEVICE = '/carwash/bay/deleteDevice'
}

// Car wash api urls
export enum CarWashApi {
  CARWASH_ADD = '/admin/company/create',
  CARWASH_EDIT = '/admin/company/editCarWash',
  GET_STATE = '/admin/company/getStateList/',
  GET_COMPANIES = 'admin/company/listCompany',
  CARWASH_OWNER_SIGNUP = 'carwash/company/signUp',
  UPDATE_INITIAL_PAYMENT_DATE = '/admin/company/updateInitialPaymentDate',
  DELETE_COMPANY = 'admin/company/delete-carwash/',
}

export enum SubscriptionApi {
  LIST_SUBSCRIPTIONS = '/admin/subscription/list',
  ADD_SUBSCRIPTION = '/admin/subscription/create',
  GET_SUBSCRIPTION_TYPES = '/admin/subscription/list',
  GET_SUBSCRIPTION_DETAILS = '/admin/subscription/detailsById/',
  GET_RELAY_DETAILS = '/admin/subscription/relayDetailsById/',
  SAVE_RELAY_NUMBER = '/admin/subscription/saveRelayNumber',
  SUBSCRIPTION_EDIT = '/admin/subscription/edit',
  GET_SUBSCRIPTION_LIST_BY_LOCATION = '/admin/subscription/getSubscriptionList/',
  CARWASH_OWNER_LIST_SUBSCRIPTIONS = '/carwash/subscription/list',
  CARWASH_OWNER_ADD_SUBSCRIPTION = '/carwash/subscription/create',
  CARWASH_OWNER_GET_RELAY_DETAILS = '/carwash/subscription/relayDetailsById/',
  CARWASH_OWNER_SAVE_RELAY_NUMBER = '/carwash/subscription/saveRelayNumber',
  CARWASH_OWNER_GET_SUBSCRIPTION_DETAILS = '/carwash/subscription/detailsById/',
  CARWASH_OWNER_SUBSCRIPTION_EDIT = '/carwash/subscription/edit',
  GET_OWNER_SUBSCRIPTION_LIST = '/admin/subscription/ownerSubscriptionList',
  CREATE_OWNER_SUBSCRIPTION = '/admin/subscription/createOwnerSubscription',
  GET_OWNER_SUBSCRIPTION_DETAILS = '/admin/subscription/getOwnerSubscriptionDetails/',
  EDIT_OWNER_SUBSCRIPTION = '/admin/subscription/editOwnerSubscription',
  DELETE_OWNER_SUBSCRIPTION = '/admin/subscription/deleteSubscription',
  UPDATE_INITIAL_FEE = '/admin/subscription/updateInitialFee',
  CARWASH_OWNER_GET_SUBSCRIPTION_LIST_BY_LOCATION = '/carwash/subscription/getSubscriptionList/',
  ADD_BULK_SINGLE_WASH = "/admin/subscription/createBulkWash",
  EDIT_BULK_SINGLE_WASH = "/admin/subscription/createBulkWash",
  CARWASH_OWNER_ADD_BULK_SINGLE_WASH = "/carwash/subscription/createBulkWash",
  CARWASH_OWNER_EDIT_BULK_SINGLE_WASH = "/carwash/subscription/editBulkWash",
  DELETE_SINGLE_WASH = "/admin/subscription/deleteSingleWash",
  CARWASH_OWNER_DELETE_SINGLE_WASH = "/carwash/subscription/deleteSingleWash",
  CARWASH_OWNER_ADD_BUY_UPS = "/carwash/buyup/createBuyup",
  CARWASH_OWNER_LIST_BUY_UPS = "/carwash/buyup/listBuyups",
  CARWASH_OWNER_GET_BUY_UP_DETAILS = "/carwash/buyup/getBuyupDetails/",
  CARWASH_OWNER_GET_BUY_UP_RELAY_DETAILS = "/carwash/buyup/relayDetailsById/",
  CARWASH_OWNER_UPDATE_BUY_UPS = "/carwash/buyup/editBuyup",
  CARWASH_OWNER_SAVE_BUY_UP_RELAY_NUMBER = "/carwash/buyup/saveRelayNumber",
  CARWASH_OWNER_DELETE_BUY_UP = "/carwash/buyup/deleteBuyup",
  GET_BUY_UP_DETAILS = "/admin/buyup/getBuyupDetails/",
  GET_BUY_UP_RELAY_DETAILS = "/admin/buyup/relayDetailsById/",
  ADD_BUY_UPS = "/admin/buyup/createBuyup",
  UPDATE_BUY_UPS = "/admin/buyup/editBuyup",
  SAVE_BUY_UP_RELAY_NUMBER = "/admin/buyup/saveRelayNumber",
  DELETE_BUY_UPS = "/admin/buyup/deleteBuyup",
  LIST_BUY_UPS = "/admin/buyup/listBuyups",
}

// Subscription api urls
export enum DashBoardApi {
  DASHBOARD_LIST = '/admin/getAdminDashboard',
  DASHBOARD_FILTER_COMPANY_LIST = '/admin/company/all-companies',
  DASHBOARD_FILTER_LOCATION_LIST = '/admin/location/company-locations',
  SYNCH_DEVICE_STATUS = '/admin/bay/updateHardwareStatus',
  CARWASH_OWNER_DASHBOARD_LIST = '/carwash/company/dashboard',
  CARWASH_OWNER_DASHBOARD_FILTER_LOCATION_LIST = '/carwash/location/getLocations',
  SYNCH_DEVICE_STATUS_V2 = "/admin/bay/updateDevices",
}


export enum HardwareApi {
  HARDWARE_LIST = '/admin/bay/getHardwares',
  CARWASH_OWNER_HARDWARE_LIST = '/carwash/bay/getHardwares'
}

export enum AdminOrderHardwareApi {
  HARDWARE_ORDER_DETAILS = '/admin/order/getOrderDetails',
  ORDER_LIST = '/admin/order/getHardwareOrderlist',
  UPDATE_ORDER_STATUS = '/admin/order/updateOrderStatus',
  WASH_BAY_DEVICE_LIST = '/admin/order/getWashBayDevices',
  ASSIGN_DEVICES = '/admin/order/allocateDevicesToOrder',
  REPLACE_ORDER_DEVICE = '/admin/order/replaceDeviceInOrder',

}

export enum OrderHardwareApi {
  CARWASH_OWNER_HARDWARE_ORDER_DETAILS = '/carwash/order/getOrderDetails',
  CARWASH_OWNER_LIST_HARDWARE = '/carwash/order/hardwares',
  CARWASH_OWNER_LIST_PACKAGE = '/carwash/order/packages',
  CARWASH_OWNER_PURCHASE_HARDWARE = 'carwash/order/purchaseHardware',
  CARWASH_OWNER_ORDER_SETTINGS = '/carwash/order/settings',
  CARWASH_OWNER_ORDER_LIST = '/carwash/order/getHardwareOrderlist'

}

export enum ManageHardwareApi {
  LIST_HARDWARE = '/admin/hardware/listHardware',
  LIST_PACKAGE = '/admin/package/listPackage',
  ADD_HARDWARE = '/admin/hardware/create',
  UPDATE_HARDWARE = '/admin/hardware/modify',
  HARDWARE_DETAILS = '/admin/hardware/detailsById/',
}

// Authorization api urls.
export enum ProfileApi {
  FETCH_PROFILE_API = '/admin/profileInfo',
  PROFILE_UPDATE_API = '/admin/modifyProfile',
  PASSWORD_UPDATE_API = '/admin/modifyPassword',
  GET_USER_PROFILE = '/user/getUserProfile'
}

export enum UsersApi {
  GET_PWA_USERS = '/admin/company/listCompanyUsers',
  CARWASH_OWNER_GET_PWA_USERS = '/carwash/company/listCompanyUsers',
  CARWASH_OWNER_MODIFY_PASSWORD = '/carwash/company/modifyPassword',
  CARWASH_OWNER_MODIFY_BANK = '/carwash/location/saveACHBankAccount',
  CARWASH_OWNER_MODIFY_PROFILE = '/carwash/company/editCarwash',
  CARWASH_OWNER_GET_BANK = '/carwash/location/getACHBankAccount',
  CARWASH_OWNER_GET_CLIENT_SECRET = '/carwash/location/setUpIntentBankAccount',
  CARWASH_OWNER_BANK_VERIFICATION_CHECKING = '/carwash/location/setUpIntentDetails',
  CARWASH_OWNER_SEND_MESSAGE = '/carwash/company/sendMessageToUsers',
}

// Promotion api urls.
export enum PromotionApi {
  GET_PROMOTIONS = '/admin/promotion/getPromotions/',
  CREATE_PROMOTION = '/admin/promotion/createPromotion',
  EDIT_PROMOTION = '/admin/promotion/editPromotion',
  GET_PROMOTION_DETAILS = '/admin/promotion/getPromotionDetails/',
  DELETE_PROMOTION = '/admin/promotion/deletePromotion',
  CARWASH_OWNER_GET_PROMOTIONS = '/carwash/promotion/getPromotions',
  CARWASH_OWNER_DELETE_PROMOTION = '/carwash/promotion/deletePromotion',
  CARWASH_OWNER_GET_PROMOTION_DETAILS = '/carwash/promotion/getPromotionDetails',
  CARWASH_OWNER_EDIT_PROMOTION = '/carwash/promotion/editPromotion',
  CARWASH_OWNER_CREATE_PROMOTION = '/carwash/promotion/createPromotion',
}

export enum RevenueApi {
  REVENUE_BY_COMPANY = '/admin/company/listUsersRevenueByCompany',
  CARWASH_OWNER_REVENUE_BY_COMPANY = '/carwash/company/listUsersRevenueByCompany',
  CARWASH_OWNER_REVENUE_SUMMARY_REPORT = '/carwash/company/getRevenueSummaryReport',
  SUPERADMIN_REVENUE_REPORT = '/admin/getRevenueReport',
  CARWASH_OWNER_PAX_REVENUE_REPORT = '/carwash/company/getPaxWashReport',
  CARWASH_OWNER_SINGLE_WASH_PURCHASE_REPORT = '/carwash/company/getSingleWashPurchaseReport',
  SUPERADMIN_SINGLE_WASH_PURCHASE_REPORT = '/admin/getSingleWashPurchaseReport',
  // TODO: Replace with actual API endpoints when provided
  CARWASH_OWNER_MEMBERSHIP_PURCHASE_REPORT = '/carwash/company/getMembershipPurchaseReport', // Placeholder
  CARWASH_OWNER_USER_ACTIVITY_REPORT = '/carwash/company/getUserActivityReport', // Placeholder
  CARWASH_OWNER_MEMBERSHIP_MONTHLY_REPORT = '/carwash/company/getMonthlyMembershipOverviewReport', // Placeholder
  CARWASH_OWNER_FAMILY_FLEET_PLAN = '/carwash/company/getFamilyMembershipReport',
  CARWASH_OWNER_MEMBERSHIP_REPORT = '/carwash/company/getMembershipReport',
  CARWASH_OWNER_USER_VISIT_DETAILS_REPORT = '/carwash/company/getUserVisitDetailsReport',
  CARWASH_OWNER_LICENSE_PLATE_NUMBERS = '/carwash/company/getLicensePlateNumbers',

}

export enum MultiLocationApi {
  MULTI_LOCATION_REPORT = '/carwash/company/getMultiLocationReport',
  MULTI_LOCATION_USER_DETAILS='/carwash/company/getMultiLocationUserWiseDetails'
}

export enum CommonApi {
  PRESIGNED_URL = '/admin/generatePresignedUrl',
  CARWASH_OWNER_PRESIGNED_URL = '/carwash/company/generatePresignedUrl'
}

// App settings  api urls
export enum AppsettingshApi {
  APP_SETTINGS_ADD = '/admin/company/saveAppSettings',
  CARWASH_OWNER_APP_SETTINGS_ADD = '/carwash/company/saveAppSettings',
  GET_APP_SETTINGS = 'admin/company/get-app-settings/',
  CARWASH_OWNER_GET_APP_SETTINGS = 'carwash/company/get-app-settings',
}


export enum PackageApi {
  LIST_PACKAGE = '/admin/package/listPackage',
  CREATE_PACKAGE = '/admin/package/create',
  GET_PACKAGE_DETAILS = '/admin/package/detailsById/',
  MODIFY_PACKAGE = '/admin/package/modify'
}

export enum TransactionApi {
  CARWASH_OWNER_GET_TRANSACTION = '/carwash/company/getTransactionReport'
}

export enum TicketApi {
  CARWASH_OWNER_CREATE_TICKET = '/carwash/customersupport/createTicket',
  CARWASH_OWNER_POST_MESSAGE = '/carwash/customersupport/postMessage',
  CARWASH_OWNER_GET_TICKET_LIST = '/carwash/customersupport/getSupportTicketList',
  CARWASH_OWNER_GET_TICKET_DETAILS = '/carwash/customersupport/getSupportTicketDetails/',
  CARWASH_OWNER_UPDATE_TICKET_STATUS = '/carwash/customersupport/updateTicketStatus',
  ADMIN_POST_MESSAGE = '/admin/customersupport/postMessage',
  ADMIN_GET_TICKET_LIST = '/admin/customersupport/getSupportTicketList',
  ADMIN_GET_TICKET_DETAILS = '/admin/customersupport/getSupportTicketDetails/',
  ADMIN_UPDATE_TICKET_STATUS = '/admin/customersupport/updateTicketStatus'
}

export enum CWOEmailTemplateApi {
  LIST_EMAIL_TEMPLATE = '/carwash/template/list',
  VIEW_EMAIL_TEMPLATE = '/carwash/template/detailsById',
  UPDATE_TEMPLATE = '/carwash/template/saveDetails'
}

export enum AdvertisementApi {
  LIST_ADVERTISEMENT = '/admin/advertisement/getAdvertisements/',
  CREATE_ADVERTISEMENT = '/admin/advertisement/createAdvertisement',
  GET_ADVERTISEMENT_DETAILS = '/admin/advertisement/getAdvertisementDetails/',
  MODIFY_ADVERTISEMENT = '/admin/advertisement/editAdvertisement',
  DELETE_ADVERTISEMENT = '/admin/advertisement/deleteAdvertisement',

  CARWASH_OWNER_LIST_ADVERTISEMENT = '/carwash/advertisement/getAdvertisements',
  CARWASH_OWNER_CREATE_ADVERTISEMENT = '/carwash/advertisement/createAdvertisement',
  CARWASH_OWNER_GET_ADVERTISEMENT_DETAILS = '/carwash/advertisement/getAdvertisementDetails/',
  CARWASH_OWNER_MODIFY_ADVERTISEMENT = '/carwash/advertisement/editAdvertisement',
  CARWASH_OWNER_DELETE_ADVERTISEMENT = '/carwash/advertisement/deleteAdvertisement'
}

export enum CampaignApi {
  LIST_CAMPAIGNS = '/carwash/campaign/getDetails',
  GET_CAMPAIGN_DETAILS_BY_ID = '/carwash/campaign/getDetailsById/',
  CREATE_CAMPAIGNS = '/carwash/campaign/create',
  EDIT_CAMPAIGN = '/carwash/campaign/modify',
  DELETE_CAMPAIGN = '/carwash/campaign/delete',
  CARWASH_OWNER_GET_COMPANY_CAMPAIGNS = '/carwash/campaign/getCampaignList',
  CARWASH_OWNER_GET_CONVERSION_REPORT = '/carwash/campaign/getCampaignReport',
  GET_CAMPAIGN_REPORT = '/carwash/campaign/getMultipleCodeCampaignReport',
  GET_REDEEMED_API = '/carwash/campaign/getRedeemedCampaignCodeReport/'
}

export enum SigngenApi {
  CARWASH_OWNER_GET_DETAILS_FOR_SIGN = '/carwash/bay/getCompanyDetails/',
  GET_DETAILS_FOR_SIGN = '/admin/bay/getCompanyDetails/'
}

export enum SubAdminApi {
  ADD_ADMIN_USER = '/admin/createAdminUser',
  LIST_ADMIN_USER = '/admin/getAdminUsersList',
  DELETE_ADMIN_USER = '/admin/deleteAdminUser',
  EDIT_ADMIN_USER = '/admin/editAdminUser',
  VIEW_ADMIN_DETAILS = '/admin/adminUserDetails/',
}

export enum ReferralApi {
  CARWASH_SAVE_REFERRAL = '/carwash/referral/save',
  CARWASH_GET_REFERRAL_SETTINGS = '/carwash/referral/get-settings'

}

export enum LocationManagerApi {
  CARWASH_OWNER_CREATE_LOCATIONMANAGER = '/carwash/locationManager/create',
  CARWASH_OWNER_GET_LOCATIONMANAGER = '/carwash/locationManager/detailsById/',
  CARWASH_OWNER_EDIT_LOCATIONMANAGER = '/carwash/locationManager/edit',
  CARWASH_OWNER_LIST_LOCATIONMANAGER = '/carwash/locationManager/list',
  CARWASH_OWNER_DELETE_LOCATIONMANAGER = '/carwash/locationManager/delete',
  CARWASH_OWNER_GET_LOCATIONMANAGER_PROFILE='carwash/company/profileInfo',
  CARWASH_OWNER_LOCATIONMANAGER_PROFILE_UPDATE='carwash/company/modifyProfile'
}

export enum TextSettingsApi {
  CARWASH_OWNER_SAVE_TEXT_SETTINGS = '/carwash/company/salesMessageToken',
  CARWASH_OWNER_GET_TEXT_SETTINGS = '/carwash/company/messageDetails'
}

export enum GiftCardApi {
  CREATE = '/carwash/giftCard/create',
  LIST = '/carwash/giftCard/list',
  DETAILS = '/carwash/giftCard/details/',
  EDIT = '/carwash/giftCard/edit',
  DELETE = '/carwash/giftCard/delete',
}
